import React, { useMemo, useState } from "react";
import { CreditCard, Banknote, Gift, X, Plus, Calculator, CheckCircle } from "lucide-react";
import { PaymentMethod } from "../types";

interface SplitPaymentProps {
  totalAmount: number;
  onComplete: (payments: PaymentMethod[], change: number) => void;
  onCancel: () => void;
}

function cx(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(" ");
}

function money(n: number) {
  return `£${Number(n || 0).toFixed(2)}`;
}

const SplitPayment: React.FC<SplitPaymentProps> = ({ totalAmount, onComplete, onCancel }) => {
  const [payments, setPayments] = useState<PaymentMethod[]>([]);
  const [currentPayment, setCurrentPayment] = useState<Partial<PaymentMethod>>({
    type: "cash",
    amount: 0,
  });
  const [cashReceived, setCashReceived] = useState<number>(0);

  const paymentTypes = useMemo(
    () => [
      { type: "cash" as const, label: "Cash", icon: Banknote },
      { type: "card" as const, label: "Card", icon: CreditCard },
      { type: "gift_card" as const, label: "Gift Card", icon: Gift },
    ],
    []
  );

  const totalPaid = payments.reduce((sum, payment) => sum + payment.amount, 0);
  const remaining = Math.max(0, totalAmount - totalPaid);

  // For cash: we compute change based on how much cash is received vs what's still remaining
  const change = currentPayment.type === "cash" ? Math.max(0, cashReceived - remaining) : 0;

  const addPayment = () => {
    if (!currentPayment.type || !currentPayment.amount || currentPayment.amount <= 0) return;

    const payment: PaymentMethod = {
      type: currentPayment.type,
      amount: Math.min(currentPayment.amount, remaining),
      reference: currentPayment.reference,
      change_given: currentPayment.type === "cash" ? change : 0,
    };

    setPayments([...payments, payment]);
    setCurrentPayment({ type: "cash", amount: 0 });
    setCashReceived(0);
  };

  const removePayment = (index: number) => {
    setPayments(payments.filter((_, i) => i !== index));
  };

  const completePayment = () => {
    if (remaining > 0.01) return; // allow small rounding differences
    onComplete(payments, change);
  };

  const quickAmounts = [5, 10, 20, 50];

  const paymentLabel = (t: PaymentMethod["type"]) => {
    const pt = paymentTypes.find((p) => p.type === t);
    return pt?.label ?? "Payment";
  };

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onCancel} />

      {/* Modal */}
      <div className="relative h-full w-full flex items-center justify-center p-4">
        <div className="w-full max-w-3xl max-h-[92vh] overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200">
          {/* Header */}
          <div className="p-5 border-b border-slate-200 bg-white">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-sm text-slate-500">Payment</div>
                <h2 className="text-xl sm:text-2xl font-extrabold tracking-tight text-slate-900">
                  Split Payment
                </h2>
              </div>

              <button
                onClick={onCancel}
                className="rounded-xl p-2 ring-1 ring-slate-200 hover:bg-slate-50 active:bg-slate-100 transition"
                title="Close"
              >
                <X size={20} className="text-slate-700" />
              </button>
            </div>

            {/* Totals */}
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="rounded-2xl bg-slate-50 ring-1 ring-slate-200 p-4">
                <div className="text-xs font-semibold text-slate-500">Total</div>
                <div className="mt-1 text-2xl font-extrabold text-slate-900">{money(totalAmount)}</div>
              </div>
              <div className="rounded-2xl bg-emerald-50 ring-1 ring-emerald-200 p-4">
                <div className="text-xs font-semibold text-emerald-700">Paid</div>
                <div className="mt-1 text-2xl font-extrabold text-emerald-700">{money(totalPaid)}</div>
              </div>
              <div className="rounded-2xl bg-amber-50 ring-1 ring-amber-200 p-4">
                <div className="text-xs font-semibold text-amber-800">Remaining</div>
                <div className="mt-1 text-2xl font-extrabold text-amber-800">{money(remaining)}</div>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="p-5 overflow-y-auto max-h-[calc(92vh-170px)]">
            {/* Existing payments */}
            {payments.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-slate-900">Applied Payments</h3>
                  <button
                    onClick={() => setPayments([])}
                    className="text-xs font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg px-2 py-1 transition"
                  >
                    Clear all
                  </button>
                </div>

                <div className="space-y-2">
                  {payments.map((payment, index) => {
                    const pt = paymentTypes.find((p) => p.type === payment.type);
                    const Icon = pt?.icon ?? CreditCard;

                    return (
                      <div
                        key={index}
                        className="rounded-2xl bg-white ring-1 ring-slate-200 shadow-sm p-3 flex items-center justify-between gap-3"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="h-10 w-10 rounded-2xl bg-slate-900 text-white flex items-center justify-center shrink-0">
                            <Icon size={18} />
                          </div>

                          <div className="min-w-0">
                            <div className="font-semibold text-slate-900">
                              {paymentLabel(payment.type)}
                            </div>
                            {payment.reference ? (
                              <div className="text-xs text-slate-500 truncate">
                                Ref: {payment.reference}
                              </div>
                            ) : (
                              <div className="text-xs text-slate-500">—</div>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-3 shrink-0">
                          <div className="text-sm font-extrabold text-emerald-700">{money(payment.amount)}</div>
                          <button
                            onClick={() => removePayment(index)}
                            className="rounded-xl p-2 ring-1 ring-slate-200 hover:bg-slate-50 active:bg-slate-100 transition"
                            title="Remove"
                          >
                            <X size={16} className="text-rose-600" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Add payment */}
            {remaining > 0.01 && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-slate-900 mb-3">Add Payment</h3>

                {/* Type selection */}
                <div className="grid grid-cols-3 gap-3 mb-4">
                  {paymentTypes.map(({ type, label, icon: Icon }) => {
                    const selected = currentPayment.type === type;
                    return (
                      <button
                        key={type}
                        onClick={() => setCurrentPayment({ ...currentPayment, type })}
                        className={cx(
                          "rounded-2xl p-3 ring-1 transition text-left",
                          selected
                            ? "bg-slate-900 text-white ring-slate-900 shadow-sm"
                            : "bg-slate-50 text-slate-900 ring-slate-200 hover:bg-slate-100"
                        )}
                      >
                        <div className="flex items-center gap-2">
                          <div
                            className={cx(
                              "h-9 w-9 rounded-2xl flex items-center justify-center",
                              selected ? "bg-white/15" : "bg-white ring-1 ring-slate-200"
                            )}
                          >
                            <Icon size={18} className={selected ? "text-white" : "text-slate-700"} />
                          </div>
                          <div className="font-semibold">{label}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Amount */}
                <div className="rounded-2xl bg-white ring-1 ring-slate-200 shadow-sm p-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-2">Amount</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-semibold">
                          £
                        </span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max={remaining}
                          value={currentPayment.amount || ""}
                          onChange={(e) =>
                            setCurrentPayment({
                              ...currentPayment,
                              amount: parseFloat(e.target.value) || 0,
                            })
                          }
                          className="w-full pl-7 pr-3 py-3 rounded-xl bg-slate-50 ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-400/50"
                          placeholder="0.00"
                        />
                      </div>

                      {/* Quick amounts */}
                      <div className="mt-3 flex flex-wrap gap-2">
                        <button
                          onClick={() => setCurrentPayment({ ...currentPayment, amount: remaining })}
                          className="rounded-xl px-3 py-1.5 text-xs font-semibold bg-slate-900 text-white hover:bg-slate-800 active:bg-slate-950 transition"
                        >
                          Exact ({money(remaining)})
                        </button>

                        {quickAmounts
                          .filter((a) => a <= remaining + 10)
                          .map((amount) => (
                            <button
                              key={amount}
                              onClick={() => setCurrentPayment({ ...currentPayment, amount })}
                              className="rounded-xl px-3 py-1.5 text-xs font-semibold bg-slate-100 text-slate-800 hover:bg-slate-200 active:bg-slate-300 transition"
                            >
                              £{amount}
                            </button>
                          ))}
                      </div>
                    </div>

                    {/* Cash received */}
                    {currentPayment.type === "cash" ? (
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-2">Cash Received</label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-semibold">
                            £
                          </span>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={cashReceived || ""}
                            onChange={(e) => setCashReceived(parseFloat(e.target.value) || 0)}
                            className="w-full pl-7 pr-3 py-3 rounded-xl bg-slate-50 ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-400/50"
                            placeholder="0.00"
                          />
                        </div>

                        {change > 0 && (
                          <div className="mt-3 rounded-2xl bg-amber-50 ring-1 ring-amber-200 p-3">
                            <div className="flex items-center gap-2">
                              <Calculator size={16} className="text-amber-700" />
                              <div className="text-sm font-semibold text-amber-800">
                                Change to give: {money(change)}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-2">
                          Reference (Optional)
                        </label>
                        <input
                          type="text"
                          value={currentPayment.reference || ""}
                          onChange={(e) =>
                            setCurrentPayment({
                              ...currentPayment,
                              reference: e.target.value,
                            })
                          }
                          className="w-full px-3 py-3 rounded-xl bg-slate-50 ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-400/50"
                          placeholder="Transaction reference"
                        />
                        <div className="mt-2 text-xs text-slate-500">
                          Used for reconciliation (card / gift card).
                        </div>
                      </div>
                    )}
                  </div>

                  <button
                    onClick={addPayment}
                    disabled={!currentPayment.amount || currentPayment.amount <= 0}
                    className={cx(
                      "mt-4 w-full rounded-2xl py-3 font-bold inline-flex items-center justify-center gap-2 transition shadow-sm",
                      !currentPayment.amount || currentPayment.amount <= 0
                        ? "bg-slate-200 text-slate-500 cursor-not-allowed"
                        : "bg-slate-900 text-white hover:bg-slate-800 active:bg-slate-950"
                    )}
                  >
                    <Plus size={18} />
                    Add Payment
                  </button>
                </div>
              </div>
            )}

            {/* Complete */}
            {remaining <= 0.01 && (
              <div className="rounded-2xl bg-emerald-50 ring-1 ring-emerald-200 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-2xl bg-emerald-600 text-white flex items-center justify-center">
                      <CheckCircle size={18} />
                    </div>
                    <div>
                      <div className="font-semibold text-emerald-900">Payment Complete</div>
                      <div className="text-sm text-emerald-700">All amounts have been collected.</div>
                    </div>
                  </div>

                  <button
                    onClick={completePayment}
                    className="rounded-2xl px-5 py-2.5 bg-emerald-600 text-white font-bold hover:bg-emerald-700 active:bg-emerald-800 transition shadow-sm"
                  >
                    Complete
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-slate-200 bg-white flex items-center justify-end gap-2">
            <button
              onClick={onCancel}
              className="rounded-xl px-4 py-2 font-semibold bg-white ring-1 ring-slate-200 hover:bg-slate-50 active:bg-slate-100 transition"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SplitPayment;
